// Reading-host adapter: all syntax and rendering remain Rust; GraphQL uses the caller's executor.
// One pass: the include tree is read level by level, each level's records in two batched requests (their kinds and
// render, then their text), and every Doc's query runs at once; a Doc renders after its includes.
export type Rec = Record<string, any>
export type Query = (query: string, vars?: Rec) => Promise<Rec>
export const sourceOf = (record: Rec, field = "body") => `---\n${JSON.stringify(record.render == null ? {} : { render: record.render })}\n---\n${record[field] ?? ""}`
const NAME = /^[_A-Za-z][_A-Za-z0-9]*$/
export function renderer(call: (v: Rec) => Rec | Promise<Rec>, q: Query) {
  const inspect = (source: string, vars: Rec) => call({ source, vars, inspect: true })
  const markdown = new Map<string, string>()
  /** Records by id, each with the field asked for (or its kind's markdown field): two requests for all of them. */
  const readAll = async (want: [string, string | undefined][]): Promise<Map<string, { record: Rec; field: string }>> => {
    const out = new Map<string, { record: Rec; field: string }>()
    if (!want.length) return out
    const ids = [...new Set(want.map(([id]) => id))]
    const head = await q(`query(${ids.map((_, i) => `$i${i}: ID!`).join(", ")}) { ${ids.map((_, i) => `e${i}: entity(id: $i${i}) { id kind __typename render { query } }`).join(" ")} }`, Object.fromEntries(ids.map((id, i) => [`i${i}`, id])))
    const ents = new Map(ids.map((id, i) => [id, head[`e${i}`] as Rec | null]))
    for (const [id, e] of ents) if (!e) throw new Error(`render reference not visible: ${id}`)
    const kinds = [...new Set([...ents.values()].map((e) => e!.kind))].filter((k) => !markdown.has(k))
    if (kinds.length) {
      const { kinds: k } = await q("query($k: [String!]) { kinds(filter: {name: {in: $k}}, page: {limit: 500}) { nodes { name info } } }", { k: kinds })
      for (const n of k.nodes) { const f = n.info.fields.find((f: Rec) => f.type === "markdown")?.name; if (f) markdown.set(n.name, f) }
    }
    const fields = want.map(([id, field]) => {
      const e = ents.get(id)!, f = field ?? markdown.get(e.kind)
      if (!f) throw new Error(`included record has no markdown field: ${id}`)
      if (!NAME.test(f)) throw new Error("invalid ref field")
      return [id, f, e] as const
    })
    const body = await q(`query(${fields.map((_, i) => `$i${i}: ID!`).join(", ")}) { ${fields.map(([, f, e], i) => `f${i}: entity(id: $i${i}) { ... on ${e.__typename} { ${f} } }`).join(" ")} }`, Object.fromEntries(fields.map(([id], i) => [`i${i}`, id])))
    fields.forEach(([id, f, e], i) => {
      const v = body[`f${i}`]?.[f]
      if (v == null) throw new Error(`ref field not visible: ${id} ${f}`)
      out.set(`${id} ${field(want[i]![1])}`, { record: { ...e, [f]: v }, field: f })
    })
    return out
  }
  const field = (f?: string) => f ?? ""
  type Node = { source: string; id?: string; path: string[]; p?: Rec; partials: Rec; refs: Rec; data?: Rec }
  const render = async (source: string, vars: Rec = {}, context?: Rec, path: string[] = []): Promise<string> => {
    const root: Node = { source, path, partials: {}, refs: {} }
    const all: Node[] = [root]
    let level: Node[] = [root]
    while (level.length) {
      for (const n of level) n.p = await inspect(n.source, vars)
      const want: [string, string | undefined][] = []
      for (const n of level) {
        for (const id of n.p!.uses) {
          if (n.path.includes(id) || n.path.length >= 64) throw new Error("render include cycle or depth exceeds 64")
          want.push([id, undefined])
        }
        for (const [id, f] of n.p!.refFields) want.push([id, f])
      }
      const got = await readAll(want)
      const next: Node[] = []
      for (const n of level) {
        for (const [id, f] of n.p!.refFields) (n.refs[id] ??= {})[f] = got.get(`${id} ${f}`)!.record[f]
        for (const id of n.p!.uses) {
          const item = got.get(`${id} `)!
          const c: Node = { source: sourceOf(item.record, item.field), id, path: [...n.path, id], partials: {}, refs: {} }
          n.partials[id] = c
          next.push(c)
          all.push(c)
        }
      }
      level = next
    }
    await Promise.all(all.map(async (n) => { n.data = n.p!.query ? await q(n.p!.query, vars) : n === root ? context ?? vars : vars }))
    const text = async (n: Node): Promise<string> => {
      const partials: Rec = {}
      for (const [id, c] of Object.entries(n.partials)) partials[id] = await text(c as Node)
      return (await call({ source: n.source, vars, data: { ...n.data, $refs: n.refs }, partials })).text
    }
    return text(root)
  }
  const record = async (id: string, f?: string, vars: Rec = {}, path: string[] = []): Promise<string> => {
    if (path.includes(id) || path.length > 64) throw new Error("render include cycle or depth exceeds 64")
    const item = (await readAll([[id, f]])).get(`${id} ${field(f)}`)!
    return render(sourceOf(item.record, item.field), vars, undefined, [...path, id])
  }
  const read = async (id: string, f?: string) => (await readAll([[id, f]])).get(`${id} ${field(f)}`)!
  return { render, record, read }
}
